import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { requestFreshSignedMedia, SignedMediaRequest } from './signedMedia'

interface FreshMediaUrl {
  mediaUrl: string
  isMediaLoading: boolean
  mediaError: boolean
  videoRef: RefObject<HTMLVideoElement>
  handleMediaError: () => void
  handleMediaLoaded: () => void
  retryMedia: () => void
}

const useFreshMediaUrl = (mediaId: number, request: SignedMediaRequest): FreshMediaUrl => {
  const [mediaUrl, setMediaUrl] = useState('')
  const [isMediaLoading, setIsMediaLoading] = useState(true)
  const [mediaError, setMediaError] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const requestVersionRef = useRef(0)
  const automaticRetriesRef = useRef(0)
  const resumeAtRef = useRef(0)

  const loadMedia = useCallback(
    async (resetRetry: boolean) => {
      const requestVersion = requestVersionRef.current + 1
      requestVersionRef.current = requestVersion

      if (resetRetry) {
        automaticRetriesRef.current = 0
        resumeAtRef.current = 0
      }

      setIsMediaLoading(true)
      setMediaError(false)

      try {
        const media = await requestFreshSignedMedia(request)
        if (requestVersion !== requestVersionRef.current) return
        setMediaUrl(media.signedUrl)
      } catch (error) {
        if (requestVersion !== requestVersionRef.current) return
        setMediaError(true)
      } finally {
        if (requestVersion === requestVersionRef.current) setIsMediaLoading(false)
      }
    },
    [request]
  )

  useEffect(() => {
    loadMedia(true)

    return () => {
      requestVersionRef.current += 1
    }
  }, [loadMedia, mediaId])

  const handleMediaError = useCallback(() => {
    if (automaticRetriesRef.current >= 1) {
      setMediaError(true)
      return
    }

    resumeAtRef.current = videoRef.current?.currentTime || 0
    automaticRetriesRef.current += 1
    loadMedia(false)
  }, [loadMedia])

  const handleMediaLoaded = useCallback(() => {
    const player = videoRef.current
    if (!player || resumeAtRef.current <= 0) return

    player.currentTime = Math.min(resumeAtRef.current, player.duration || resumeAtRef.current)
    resumeAtRef.current = 0
    player.play().catch(() => undefined)
  }, [])

  const retryMedia = useCallback(() => loadMedia(true), [loadMedia])

  return {
    mediaUrl,
    isMediaLoading,
    mediaError,
    videoRef,
    handleMediaError,
    handleMediaLoaded,
    retryMedia,
  }
}

export default useFreshMediaUrl
